import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Alert, IconButton, Tooltip } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { ModuleColumn } from '../../types';
import { invoiceAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useTableColumns } from '../../hooks/useTableColumns';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';
import { useSystemTotal } from '../../hooks/useSystemTotal';

const columns: ModuleColumn[] = [
  { field: 'invoiceNumber', header: 'Invoice #' },
  { field: 'biltyNumber', header: 'Bilty #' },
  { field: 'invoiceDate', header: 'Date', format: 'date' },
  { field: 'subtotal', header: 'Subtotal', format: 'currency' },
  { field: 'taxAmount', header: 'Tax', format: 'currency' },
  { field: 'totalAmount', header: 'Total', format: 'currency' },
  { field: 'paymentStatus', header: 'Payment' },
];

export const InvoiceListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { formatCurrency } = useLocale();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  const canView = hasPermission('invoices', 'view');
  const canPrint = hasPermission('invoices', 'print') || canView;
  const tableColumns = useTableColumns('invoices', columns);
  const { total: systemTotal, refresh: refreshTotal } = useSystemTotal('invoices');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await invoiceAPI.getAll();
      const raw = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRows(raw.map((row: Record<string, unknown>) => ({
        ...row,
        biltyNumber: (row.bilty as { biltyNumber?: string } | undefined)?.biltyNumber
          || row.biltyNumber
          || '—',
      })));
      refreshTotal();
    } catch {
      setError('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [refreshTotal]);

  useEffect(() => { load(); }, [load]);

  const displayColumns = tableColumns.columns.map((col) => (
    col.format === 'currency'
      ? { ...col, render: (row: Record<string, unknown>) => formatCurrency(Number(row[col.field] || 0)) }
      : col
  ));

  return (
    <Box>
      <PageHeader
        title="Invoices"
        subtitle={getPageSubtitle('invoices', 'Invoices', { total: systemTotal })}
        icon={<ReceiptIcon />}
        isCore

      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <DataTable
        columns={[
          ...displayColumns,
          {
            field: '_actions',
            header: 'Actions',
            sortable: false,
            render: (row: Record<string, unknown>) => (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {canView && (
                  <Tooltip title="View">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${row._id}`); }}>
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canPrint && (
                  <Tooltip title={row.bilty || row.biltyNumber && row.biltyNumber !== '—' ? 'Print LR' : 'Link bilty to print LR'}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${row._id}?print=1`); }}>
                      <PrintOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ),
          },
        ]}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onRowClick={canView ? (row) => navigate(`/invoices/${row._id}`) : undefined}
        columnPicker={{
          allColumns: tableColumns.allColumns,
          visibleFields: tableColumns.activeFields,
          defaultFields: tableColumns.defaultFields,
          onSave: tableColumns.updateColumns,
          onReset: tableColumns.resetColumns,
          isCustomized: tableColumns.isCustomized,
        }}
        canView={canView}
        enableColumnFilters
        recordLabel="invoices"
      />
    </Box>
  );
};
